import React from 'react';
import {
    OrderType,
    PromotionApi,
    PromotionConditionOperator,
    PromotionConditionType,
    PromotionEntityApi,
    PromotionEntityPromotionType,
    PromotionExpressionConditionApi
} from "../../../../services/Api/types";
import {RootState} from "../../../../../reducers";
import {getAvailabilities, getItemGroups, getItems} from "../../../../services/Menu/reducer";
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import {formatMoney} from "../../../Common/Money";
import i18n from "i18next";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {getStoreConfig} from "../../../../services/Store/reducer";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import {selectLocale} from "../../../../services/Session/reducer";
import {formatDate, getDayOfWeek, isTimeAllDay} from "../../../../services/Common/Date/DateHelper";

interface OwnProps {
    promotion: PromotionApi;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const PromotionInfoForNewVersion = ({
                                        promotion,
                                        items,
                                        config,
                                        availabilities,
                                        locale,
                                        itemGroups
                                    }: Props) => {
    const {t} = useTranslation();

    const renderExpressions = () => {
        if (!promotion?.expressions?.length) return undefined;

        const allConditions = promotion.expressions.map(expression => expression.conditions).flat();

        if (!allConditions.length) return undefined;

        const parsedConditions = allConditions.map(getParsedCondition);
        const parsedConditionsWithValues = parsedConditions.filter(condition => condition?.values);

        if (!parsedConditions.length) return undefined;

        if (parsedConditionsWithValues.find(condition => !condition?.values && condition?.condition?.category_id)) {
            return (
                <div className="text-center">
                    <dt>
                        {t("modules.promotion.field.does_not_meet_the_conditions.title")}
                    </dt>
                </div>
            )
        }

        return (
            <div>
                <dt>
                    {t("modules.promotion.field.conditions.title")}
                </dt>
                <dd>
                    <ul>
                        {parsedConditionsWithValues.map((condition, index: number) => {
                            return condition?.values.map((value, valueIndex: number) => {
                                const withAdditionalTab = condition?.values && condition.values.length > 1 && valueIndex > 0;
                                return (
                                    <li key={`condition-${index}-value-${valueIndex}`}
                                        className={withAdditionalTab ? "ms-3" : "ms-0"}>
                                        {value}
                                    </li>
                                )
                            })
                        })}
                    </ul>
                </dd>
            </div>
        )
    };

    const findAllItemsForCategory = (categoryId: number) => {
        return items.filter((item: Record<string, any>) => item.category_id === Number(categoryId));
    }

    const getParsedCondition = (condition: PromotionExpressionConditionApi): ({
        condition: PromotionExpressionConditionApi,
        values: string[]
    } | undefined) => {
        switch (condition.type) {
            case PromotionConditionType.ORDER_ITEM_COUNT: {
                if (condition.category_id) {
                    const categoryItems = findAllItemsForCategory(condition.category_id);

                    if (categoryItems.length === 0) return undefined;

                    const itemsNames = categoryItems.map((categoryItem: Record<string, any>) => ItemHelper.getItem(items, categoryItem.id)?.translation?.name).filter(Boolean)
                        .map((name: string) => `"${name}" ${t(`enums.condition.operator.${condition.operator}`)} ${condition.value}`);

                    return itemsNames.length === 0 ? undefined : {
                        condition,
                        values: [`${t("enums.condition.type.ORDER_ITEM_COUNT")}  ${t("common.word.for")} ${t("modules.promotion.field.for_one_of.title")}:`, ...itemsNames]
                    };
                }

                if (condition.item_id) {
                    const item = ItemHelper.getItem(items, Number(condition.item_id));

                    if (item && item.translation?.name) {
                        return {
                            condition,
                            values: [`${t("enums.condition.type.ORDER_ITEM_COUNT")} ${t("common.word.for")} "${item.translation.name}" ${t(`enums.condition.operator.${condition.operator}`)} ${condition.value}`]
                        };
                    }

                    return undefined;
                }

                if (condition.item_group_id) {
                    const itemGroup = itemGroups.find((itemGroup: Record<string, any>) => itemGroup.id === Number(condition.item_group_id));

                    if (itemGroup && itemGroup.translation?.name) {
                        return {
                            condition,
                            values: [`${t("enums.condition.type.ORDER_ITEM_COUNT")} ${t("common.word.for")} "${itemGroup.translation.name}" ${t(`enums.condition.operator.${condition.operator}`)} ${condition.value}`]
                        };
                    }

                    return undefined;
                }

                return undefined;
            }
            case PromotionConditionType.ORDER_TOTAL_PRICE:
            case PromotionConditionType.ORDER_TOTAL_PRICE_NET:
                return {
                    condition,
                    values: [`${t(`enums.condition.type.${condition.type}`)} ${t(`enums.condition.operator.${condition.operator}`)} ${formatMoney({
                        amount: Number(condition.value),
                        currency: "PLN"
                    }, i18n)}`]
                };
            case PromotionConditionType.ORDER_TYPE: {
                if (condition.operator === PromotionConditionOperator.EQUALS) {
                    return {
                        values: [`${t("enums.condition.type.ORDER_TYPE")}: ${t(`enums.order.type.${condition.value}`).toLowerCase()}`],
                        condition
                    };
                }

                const availableOrderTypes = config ? StoreUtils.getOrderTypes(config).filter(orderType => orderType !== condition.value as OrderType) : [];

                if (availableOrderTypes.length > 0) {
                    const orderTypesTitle = availableOrderTypes.map(orderType => t(`enums.order.type.${orderType}`).toLowerCase()).join(", ");

                    return {
                        values: [`${t("enums.condition.type.ORDER_TYPE")}: ${orderTypesTitle}`],
                        condition
                    };
                }

                return undefined;
            }
            default:
                return undefined;
        }
    };

    const findEntityObject = (entity: PromotionEntityApi) => {
        if (entity.type == "ITEM") {
            return ItemHelper.getItem(items, Number(entity.entity_id));
        }

        if (entity.type == "CATEGORY") {
            const categoryItems = findAllItemsForCategory(Number(entity.entity_id));
            return categoryItems.length > 0 ? categoryItems.map((categoryItem: Record<string, any>) => ItemHelper.getItem(items, categoryItem.id)).filter(Boolean) : undefined;
        }
    }

    const renderEntities = () => {
        if (!promotion?.entities?.length) return undefined;

        const parsedEntities = promotion.entities.map((entity) => {
            const entityObject = findEntityObject(entity);

            if (!entityObject) return undefined;

            const name = entityObject.translation.name;

            if (!name) return undefined;

            const value = entity.promotion_type === PromotionEntityPromotionType.PERCENT ?
                `${entity.volume} %`
                : formatMoney({amount: entity.volume, currency: "PLN"}, i18n);

            return `${name} - ${t("modules.promotion.field.discount.title")} ${t(`enums.promotion.type.${entity.promotion_type}`).toLowerCase()} ${value}`;
        }).filter(Boolean);

        if (!parsedEntities.length) return undefined;

        return (
            <div>
                <dt>
                    {t("modules.promotion.field.entities.title")}
                </dt>
                <dd>
                    <ul>
                        {parsedEntities.map((entity, index: number) => {
                            return (
                                <li key={index}>
                                    {entity}
                                </li>
                            )
                        })}
                    </ul>
                </dd>
            </div>
        )
    }

    const renderAvailability = () => {
        if (!promotion.availability_id) return undefined;

        const availability = availabilities.find((availability: Record<string, any>) => availability.id === promotion.availability_id);
        const elements: JSX.Element[] = [];

        if (availability) {
            const openingInfo: Record<string, any>[] = AvailableHelper.getFullOpeningInfo(availability);

            if (openingInfo.length) {
                if (availability.dates.length > 0) {
                    const values = openingInfo.filter((value: Record<string, any>) => value.date_from && value.date_to);
                    values.forEach((value) => {
                        elements.push(
                            <li>
                                {`${formatDate(value.date_from)} - ${formatDate(value.date_to)}`}
                            </li>
                        )
                    });
                }

                if (availability.hours.length > 0) {
                    const values = openingInfo.filter((value: Record<string, any>) => value.hour_from && value.hour_to);

                    values.forEach((value) => {
                        const allDay = isTimeAllDay(value.hour_from) && isTimeAllDay(value.hour_to);
                        const weekday = getDayOfWeek(value.day, locale, undefined, true);
                        const title = allDay ? t("common.word.whole_day") : `${value.hour_from} - ${value.hour_to}`;

                        elements.push(
                            <li>
                                {`${weekday}: ${title}`}
                            </li>
                        )
                    })
                }
            }

            if (elements.length > 0) {
                return (
                    <div>
                        <dt>
                            {t("modules.promotion.field.availability.title")}
                        </dt>
                        <dd>
                            <ul>
                                {elements.map((element, index: number) => {
                                    return (
                                        <li key={index}>
                                            {element}
                                        </li>
                                    )
                                })}
                            </ul>
                        </dd>
                    </div>
                )
            }
            return undefined;
        }

        return undefined;
    }

    return (
        <dl>
            {renderExpressions()}
            {renderEntities()}
            {renderAvailability()}
            {promotion.usage_limit && (
                <div>
                    <dt>
                        {t("modules.promotion.field.usage_limit.title")}
                    </dt>
                    <dd>
                        <ul>
                            <li>
                                {promotion.usage_limit}
                            </li>
                        </ul>
                    </dd>
                </div>
            )}
        </dl>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    config: getStoreConfig(state.config),
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
    itemGroups: getItemGroups(state.menu)
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(PromotionInfoForNewVersion));

// t("enums.condition.type.ORDER_TOTAL_PRICE");
// t("enums.condition.type.ORDER_TOTAL_PRICE_NET");

// t("enums.condition.operator.GREATER_THAN");
// t("enums.condition.operator.LESS_THAN");
// t("enums.condition.operator.LESS_THAN_OR_EQUAL_TO");
// t("enums.condition.operator.EQUALS");
// t("enums.condition.operator.NOT_EQUALS");
// t("enums.condition.operator.GREATER_THAN_OR_EQUAL_TO");

// t("enums.promotion.type.FIXED_PRICE");
// t("enums.promotion.type.AMOUNT");
// t("enums.promotion.type.PERCENT");